import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  Divider,
  TextField,
  FormControl,
  FormHelperText,
  Typography,
  Button,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import useAPI from '../../../api';
import { CenteredFlexBox } from '../../../Components/FlexBox';
import FetchAutoComplete from '../../../Components/Inputs/FetchAutoComplete';
import FormSelect from '../../../Components/Form/FormSelect';
import Select from '../../Inputs/Select';
import IconButton from '../../../Components/Button/IconButton';
import { pickerLocaleProps } from '../../../Helpers/dates';

const rootSx = {
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: 'stretch',
  width: '100%',
  gap: { xs: 1, md: 0 },
};

const eventFormSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  flex: 1,
  minWidth: 0,
  p: 2,
};

const datePickerSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  flexShrink: 0,
  p: 2,
};

const dividerSx = {
  display: { xs: 'none', md: 'block' },
};

const AddEditEventWidget = function AddEditEventWidget({
  event = null, //object containing selected event or the provided date and time from click or select action on big calendar
  selectedBranch = null, //object with branch infos
  operators,
  hideCustomerField = false,
}) {
  const API = useAPI();
  const intl = useIntl();
  const [timeUnit, setTimeUnit] = useState('MINUTES');
  const [remindBefore, setRemindBefore] = useState('10');
  const [reminder, setReminder] = useState(false);
  const {
    register, //sends input data
    formState: { errors, isSubmitSuccessful },
    control,
    reset,
    setValue,
  } = useFormContext();

  useEffect(() => {
    if (event) {
      reset({
        customer: event?.entityId
          ? {
              id: event.entityId,
              fullName: event.contactFullName,
            }
          : null,
        desc: event.desc || '',
        time: event.startDate || event.start,
        date: event.startDate || event.start,
        assignee: event?.assignee?.toLowerCase() || 'ALL',
        remindBefore: event.remindBefore || '',
      });
      if (event.remindBefore) {
        setRemindBefore(
          (event.remindBefore % 60 === 0
            ? event.remindBefore / 60
            : event.remindBefore) || ''
        );
        setTimeUnit(event.remindBefore % 60 === 0 ? 'HOURS' : 'MINUTES');
        setReminder(true);
      }
    } else {
      reset({
        customer: null,
        desc: '',
        time: null,
        date: null,
        assignee: 'ALL',
        remindBefore: '',
      });
    }
  }, [event]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({
        customer: null,
        desc: '',
        time: null,
        date: null,
        assignee: 'ALL',
        remindBefore: '',
      });
    }
  }, [isSubmitSuccessful]);

  const getContacts = (searchText, onSuccess, onFailure) =>
    API.searchContactByName(
      selectedBranch.id,
      searchText,
      {
        silent: true,
      },
      (res) => onSuccess(res),
      onFailure
    );

  const handleChangeTimeUnit = (value) => {
    setTimeUnit(value);
  };

  const handleChangeRemindBefore = (event) => {
    if (parseInt(event.target.value, 10) > 0 || event.target.value === '')
      setRemindBefore(event.target.value);
  };

  useEffect(() => {
    if (!reminder) {
      setValue('remindBefore', null);
      return;
    }
    const remindBeforeInMinutes = remindBefore
      ? parseInt(remindBefore, 10) * (timeUnit === 'HOURS' ? 60 : 1)
      : null;
    setValue('remindBefore', remindBeforeInMinutes);
  }, [remindBefore, timeUnit, reminder]);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} {...pickerLocaleProps}>
      <Box sx={rootSx}>
        <Box sx={eventFormSx}>
          {!hideCustomerField && (
            <Controller
              control={control}
              name="customer"
              defaultValue={
                event?.id
                  ? {
                      id: event.id,
                      fullName: event.contactFullName,
                    }
                  : null
              }
              rules={{ required: intl.messages.REQUIRED_FIELD || true }}
              render={({ field: { onChange, value } }) => (
                <FetchAutoComplete
                  fetchCallback={getContacts}
                  id="contacts-autocomplete"
                  noOptionsText={intl.messages.TYPE_CUSTOMER}
                  selectedValue={value}
                  onChangeSelectedValue={(_event, data) => onChange(data)}
                  label={intl.messages.CUSTOMER}
                  placeholder={intl.messages.CHOOSE_CUSTOMER}
                  error={errors?.customer}
                  helperText={errors?.customer ? errors.customer.message : null}
                  width="100%"
                  getOptionLabel={(option) => option.fullName}
                  noCustomValue
                />
              )}
            />
          )}
          <FormSelect
            name="assignee"
            defaultValue={event?.assignee || 'ALL'}
            options={[
              {
                label: intl.messages.ALL,
                value: 'ALL',
              },
              ...operators,
            ]}
            noTranslation
            required
            noHelper
          />
          {event?.type === 'Recall' ? null : (
            <>
              {!reminder ? (
                <Button
                  onClick={() => setReminder(true)}
                  variant="outlined"
                  sx={{ width: '50%' }}
                  size="large"
                >
                  {intl.messages.ADD_REMINDER}
                </Button>
              ) : (
                <CenteredFlexBox gap="8px">
                  <Typography variant="body1">
                    {intl.messages.REMINDBEFORE}
                  </Typography>
                  <TextField
                    type="number"
                    value={remindBefore}
                    onChange={handleChangeRemindBefore}
                    slotProps={{ htmlInput: { min: 0 } }}
                  />
                  <Select
                    defaultValue={timeUnit}
                    onChange={handleChangeTimeUnit}
                    options={['MINUTES', 'HOURS']}
                  />
                  <IconButton
                    id="remove-reminder"
                    onClick={() => setReminder(false)}
                  >
                    <CloseRoundedIcon />
                  </IconButton>
                </CenteredFlexBox>
              )}
            </>
          )}
          <TextField
            defaultValue={event?.desc}
            {...register('desc')}
            size="medium"
            id="description-area"
            label={intl.messages.DESCRIPTION}
            multiline
            fullWidth
            rows={hideCustomerField ? 5 : undefined}
            minRows={2}
            maxRows={5}
            placeholder={intl.messages.SET_DESCRIPTION}
            variant="filled"
          />
          <Controller
            control={control}
            name="time"
            rules={{ required: intl.messages.REQUIRED_FIELD || true }}
            defaultValue={event?.startDate || event?.start}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TimePicker
                ampm={false}
                label={intl.messages.START_TIME}
                value={value}
                onChange={onChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!error, // This forces the red border
                    helperText: error?.message, // This shows the red text below
                  },
                }}
              />
            )}
          />
        </Box>
        <Divider
          orientation="vertical"
          variant="middle"
          flexItem
          sx={dividerSx}
        />
        <Box sx={datePickerSx}>
          <Controller
            control={control}
            name="date"
            rules={{ required: intl.messages.REQUIRED_FIELD || true }}
            defaultValue={event?.startDate || event?.start}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl error={error}>
                <DateCalendar
                  onChange={onChange}
                  value={value ?? null}
                  sx={{
                    border: '1px solid',
                    borderColor: error ? 'error.main' : 'transparent', // error.main uses MUI's exact red
                    borderRadius: 1,
                  }}
                />
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AddEditEventWidget;
